import { useEffect, useRef } from 'react';

import { CHANNEL_STATUS, IChannel } from '@/entities/channel';

import { useQrCodeByChannel } from '../../model/scanQrCode/useQrCodeByChannel';
import { useLoginChannelByQr } from '../../model/useLoginChannelByQr';

import { AuthByQrCode } from './AuthByQrCode';

const QR_REFRESH_LEEWAY_MS = 10_000;
const QR_REFRESH_DEBOUNCE_MS = 10_000;
const QR_FALLBACK_REFRESH_MS = 20_000;

const getQrRefreshDelayMs = (status?: string, qrExpires?: string) => {
  if (
    status !== CHANNEL_STATUS.QR_IN_PROGRESS &&
    status !== CHANNEL_STATUS.REQUIRED_SCAN_QR
  ) {
    return null;
  }

  if (!qrExpires) return QR_FALLBACK_REFRESH_MS;

  const expiresAtMs = Date.parse(qrExpires);
  if (Number.isNaN(expiresAtMs)) return QR_FALLBACK_REFRESH_MS;

  return Math.max(0, expiresAtMs - Date.now() - QR_REFRESH_LEEWAY_MS);
};

export type TTelegramPersonalQrCodeDialogProps = {
  channel: IChannel;
  onAuthorize?: () => void;
  enabledQuery: boolean;
};

export const TelegramPersonalQrCode = ({
  channel,
  onAuthorize,
  enabledQuery,
}: TTelegramPersonalQrCodeDialogProps) => {
  const { channelLogin, isPending: isQrLoginPending } = useLoginChannelByQr();
  const shouldOnceRequest = useRef(true);
  const lastQrRefreshAt = useRef(0);
  const lastQrExpiresRequested = useRef<string | null>(null);

  // Пользователь явно выбрал авторизацию через QR — стартуем свежую QR-сессию
  // независимо от текущего статуса канала (например, после застрявшего sms_sent).
  useEffect(() => {
    if (shouldOnceRequest.current && !isQrLoginPending) {
      shouldOnceRequest.current = false;
      channelLogin(channel.id);
    }
  }, [channel.id, channelLogin, isQrLoginPending]);

  const { dataChannelById } = useQrCodeByChannel(
    channel.id,
    true,
    enabledQuery,
    onAuthorize
  );

  // Планируем обновление QR по времени его истечения.
  useEffect(() => {
    const nextStatus = dataChannelById?.status as string | undefined;
    const nextQrExpires = dataChannelById?.qr_expires;
    const refreshDelayMs = getQrRefreshDelayMs(nextStatus, nextQrExpires);

    if (!enabledQuery || refreshDelayMs === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (isQrLoginPending) return;

      const now = Date.now();
      if (
        now - lastQrRefreshAt.current < QR_REFRESH_DEBOUNCE_MS &&
        lastQrExpiresRequested.current === (nextQrExpires ?? null)
      ) {
        return;
      }

      lastQrRefreshAt.current = now;
      lastQrExpiresRequested.current = nextQrExpires ?? null;
      channelLogin(channel.id, { silent: true });
    }, refreshDelayMs);

    return () => window.clearTimeout(timer);
  }, [
    enabledQuery,
    channel.id,
    channelLogin,
    isQrLoginPending,
    dataChannelById?.status,
    dataChannelById?.qr_expires,
  ]);

  return (
    <AuthByQrCode
      qr={dataChannelById?.qr_url}
      qrExpires={dataChannelById?.qr_expires}
      id={channel.id}
    />
  );
};
